import { ButtonContainer, IDataTableColumn, ToolTipButton } from "nsitools-react";
import * as React from "react";

import { DashboardApi, DashboardAvisResultatDto, DashboardAvisResultatSearch } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { CircleColumn, CustomBulletList, SearchTablePage, ViewButton } from "../../../components";
import { useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Colors, Icon, Tooltip } from "@blueprintjs/core";
import styled from "styled-components";
import { computeContratStatutColor } from "../../../utils/contratStatutColor";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

export interface IDashboardAvisResultatProps {
}

export const DashboardAvisResultat: React.FunctionComponent<IDashboardAvisResultatProps> = props => {
  const { t, tUnsafe } = useTl();
  const { theme } = useTheme();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();

  const searchFunction = React.useCallback(
    (sObj?: DashboardAvisResultatSearch) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchDashboardAvisResultat({ DashboardAvisResultatSearch: sObj });
    },
    [apiFactory]
  );

  const [rowLoading, setRowLoading] = React.useState(null);
  const { dispatchEvent } = useEventsContext();

  const { showDialogPromise } = useDialog();
  const masquerLigne = React.useCallback(
    async (idbulletinApprenantType: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerLigneConfirmTitle),
        message: t(ETLCodes.MasquerLigneConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idbulletinApprenantType);
        await api.dashboardMasquerAvisResultat({
          idbulletinApprenantType: idbulletinApprenantType
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Inscriptions");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DashboardAvisResultatDto) => (
          <ButtonContainer>
            {!row.masque &&
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerLigne)}
                minimal
                icon="cross"
                onClick={() => masquerLigne(row.idapprenant)}
                loading={rowLoading === row.idapprenant}
                disabled={!!rowLoading && rowLoading !== row.idapprenant}
              />}
            <ViewButton
              minimal={true}
              onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/documents/view`)}
            />
          </ButtonContainer>
        )
      },
      {
        computed: true,
        alignment: "center",
        autoFitContent: true,
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.Statut)}</span>
            <Tooltip
              content={
                <CustomBulletList
                  title={t(ETLCodes.Statut)}
                  items={[
                    {
                      text: t(ETLCodes.Revalidated),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.Invalidated),
                      color: theme.dangerColor
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon
                icon="info-sign"
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          </LegendHeaderContainer>
        ),
        fieldName: "statut",
        render: (row: DashboardAvisResultatDto) => (
          <CircleColumn
            color={row.revalidated ? theme.sucessColor : theme.dangerColor}
            tooltip={t(row.revalidated ? ETLCodes.Revalidated : ETLCodes.Invalidated)}
          />
        )
      },
      {
        computed: true,
        alignment: "center",
        autoFitContent: true,
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.StatutContrat)}</span>
            <Tooltip
              content={
                <CustomBulletList
                  title={t(ETLCodes.Statut)}
                  items={[
                    {
                      text: t(ETLCodes.EnCours),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.Rompu),
                      color: theme.dangerColor
                    },
                    {
                      text: t(ETLCodes.Suspendu),
                      color: theme.warningColor
                    },
                    {
                      text: t(ETLCodes.JamaisDeContrat),
                      color: Colors.BLACK
                    },
                    {
                      text: t(ETLCodes.Termine),
                      color: Colors.VIOLET4
                    },
                    {
                      text: t(ETLCodes.SansSuite),
                      hideBullet: true
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon
                icon="info-sign"
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          </LegendHeaderContainer>
        ),
        fieldName: "statutContrat",
        render: (row: DashboardAvisResultatDto) => (
          <CircleColumn
            color={computeContratStatutColor(row.statutContrat, theme)}
            tooltip={tUnsafe(`StatutContrat_${row.statutContrat}`)}
          />
        )
      },
      {
        header: () => t(ETLCodes.AnneeScolaire),
        fieldName: "anneeScolaire"
      },
      {
        header: () => t(ETLCodes.Concerne),
        fieldName: "concerne"
      },
      {
        header: () => t(ETLCodes.MetierStade),
        fieldName: "MetierStade"
      },
      {
        header: () => t(ETLCodes.TypeCours),
        fieldName: "typeCours"
      }
    ],
    [masquerLigne, rowLoading, t, tUnsafe, theme]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      withCard={false}
      columns={columns}
      searchFunction={searchFunction}
      onAbort={onAbort}
    />
  );
};
